import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import jwtService from '@/services/jwtService'
import emitter from '@/services/emitter'
import { getCurrentLanguage } from '@/i18n'

// client_id OpenIddict-приложения. Дефолт совпадает с бэкендом Shagan (spa_app).
const oauthClientId = import.meta.env.VITE_OAUTH_CLIENT_ID || 'spa_app'

interface TokenRefreshResponse {
  access_token: string
  expires_in: number
  token_type: string
}

type InternalAxiosRequestConfigWithRetry = InternalAxiosRequestConfig & { _retry?: boolean }

// Имя файла из Content-Disposition: приоритет filename*=UTF-8''… (RFC 5987, кириллица),
// фолбэк — filename="…". null, если заголовок отсутствует/не распознан.
function parseContentDispositionFilename(header?: string): string | null {
  if (!header) return null
  const star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
    } catch {
      // Битая перкодировка — пробуем обычный filename ниже.
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i)
  return plain?.[1]?.trim() ?? null
}

class HttpClient {
  private readonly instance: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value: unknown) => void
    reject: (reason?: unknown) => void
  }> = []
  // Проактивное обновление токена: таймер на «до истечения», чтобы навигация не упиралась
  // в 401 на каждом клике (реактивный refresh остаётся страховкой). Обновляем за skew до exp.
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private readonly refreshSkewMs = 60_000

  /**
   * Конструктор HttpClient
   * @param baseURL - Базовый URL для всех запросов API
   */
  constructor(baseURL: string = '') {
    this.instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      validateStatus: (status: number) => status >= 200 && status < 500,
    })

    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        config.headers['X-Language'] = getCurrentLanguage()

        if (!config.url?.includes('/api/connect/token') && !config.headers['Authorization']) {
          const token = jwtService.getToken()
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
          }
        }
        return config
      },
      (error) => Promise.reject(error),
    )

    this.instance.interceptors.response.use(
      async (response: AxiosResponse) => {
        if (response.status === 401) {
          console.warn(
            'Intercepted 401 in onFulfilled handler (due to custom validateStatus). Attempting refresh...',
          )
          try {
            const retryResponse = await this.handleUnauthorizedAttempt(
              response.config as InternalAxiosRequestConfigWithRetry,
            )
            return retryResponse
          } catch (refreshError) {
            return Promise.reject(refreshError)
          }
        }
        return response
      },

      (error: AxiosError) => {
        console.error('onRejected handler called (status >= 500 or network error):', error)
        return Promise.reject(error)
      },
    )

    // Фоновые вкладки тормозят setTimeout → проактивный таймер может не сработать вовремя.
    // При возврате на вкладку пере-планируем (если токен близок к истечению — обновит сразу).
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && jwtService.getToken()) {
          this.scheduleProactiveRefresh()
        }
      })
    }
  } // Конец конструктора

  /**
   * Обрабатывает попытку обновления токена и повтора запроса при получении 401.
   * Используется внутри интерсептора ответа.
   * @param originalRequest - Конфигурация оригинального запроса.
   * @returns Промис с ответом от повторного запроса или отклоненный промис.
   */
  private async handleUnauthorizedAttempt(
    originalRequest: InternalAxiosRequestConfigWithRetry,
  ): Promise<AxiosResponse> {
    if (originalRequest._retry) {
      throw new Error('Refresh token attempt failed after retry.')
    }
    originalRequest._retry = true

    const newAccessToken = await this.refreshAccessToken()
    if (originalRequest.headers) {
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
    }
    return await this.instance(originalRequest)
  }

  /**
   * Единая точка обновления токена (single-flight). Сюда идут И реактивный перехват 401, И
   * проактивный таймер — параллельные вызовы сериализуются через isRefreshing + очередь.
   * Это исключает гонку двух refresh с одним ротируемым refresh-токеном (OpenIddict считает
   * это повторным использованием и рвёт цепочку → 400 → выброс).
   */
  public async refreshAccessToken(): Promise<string> {
    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        this.failedQueue.push({ resolve: (token) => resolve(token as string), reject })
      })
    }

    this.isRefreshing = true
    try {
      // refresh_token хранится в httpOnly-куку (withCredentials), поэтому его нет в теле запроса.
      const response = await axios.post<TokenRefreshResponse>(
        `${this.instance.defaults.baseURL || ''}/api/connect/token`,
        new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: oauthClientId,
        }),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          withCredentials: true,
        },
      )

      const newAccessToken = response.data.access_token
      jwtService.saveToken(newAccessToken)
      this.processQueue(null, newAccessToken)
      this.scheduleProactiveRefresh(newAccessToken)
      return newAccessToken
    } catch (refreshError: unknown) {
      jwtService.clear()
      this.processQueue(refreshError, null)
      this.clearProactiveTimer()
      this.notifyLogoutRequired('refreshFailed')
      throw refreshError
    } finally {
      this.isRefreshing = false
    }
  }

  private getTokenExpiryMs(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as { exp?: number }
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null
    } catch {
      return null
    }
  }

  /**
   * Планирует тихое обновление токена ДО истечения (за refreshSkewMs до exp). Навигация тогда
   * не упирается в 401 на каждом клике. Вызывается при логине, после каждого refresh и при
   * возврате на вкладку. Если токен уже почти протух — обновляет сразу.
   */
  public scheduleProactiveRefresh(token?: string | null): void {
    this.clearProactiveTimer()
    const current = token ?? jwtService.getToken()
    if (!current) {
      return
    }
    const expiryMs = this.getTokenExpiryMs(current)
    if (expiryMs == null) {
      return
    }
    const delay = expiryMs - Date.now() - this.refreshSkewMs
    if (delay <= 0) {
      void this.refreshAccessToken().catch(() => {})
      return
    }
    this.refreshTimer = setTimeout(() => void this.refreshAccessToken().catch(() => {}), delay)
  }

  public clearProactiveTimer(): void {
    if (this.refreshTimer != null) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  // Остановить фоновое обновление (при выходе).
  public stopSession(): void {
    this.clearProactiveTimer()
  }

  /**
   * Обрабатывает очередь ожидающих запросов.
   * @param error - Ошибка (если обновление не удалось) или null.
   * @param token - Новый токен (если обновление успешно) или null.
   */
  private processQueue(error: unknown | null, token: string | null = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    this.failedQueue = []
  }

  /**
   * Генерирует событие 'logoutRequired' через глобальный эмиттер.
   * @param reason - Опциональная причина события.
   */
  private notifyLogoutRequired(reason?: string): void {
    console.log(`Emitting logoutRequired event. Reason: ${reason || 'N/A'}`)
    emitter.emit('logoutRequired', { reason })
  }

  public async get<T = unknown>(
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.instance.get<T>(url, {
        ...config,
        params: queryParams,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams()
          for (const key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key)) {
              const value = params[key]
              if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(key, String(v)))
              } else if (value !== null && value !== undefined) {
                searchParams.append(key, String(value))
              }
            }
          }
          return searchParams.toString()
        },
      })
      return response.data
    } catch (error) {
      console.error(`GET request to ${url} failed:`, error)
      throw error
    }
  }

  // Скачивание файла: возвращает тело (Blob) и имя из Content-Disposition (для link.download).
  public async download(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<{ blob: Blob; fileName: string | null }> {
    try {
      const response = await this.instance.get<Blob>(url, {
        ...config,
        responseType: 'blob',
      })
      const fileName = parseContentDispositionFilename(
        response.headers['content-disposition'] as string | undefined,
      )
      return { blob: response.data, fileName }
    } catch (error) {
      console.error(`Download request to ${url} failed:`, error)
      throw error
    }
  }

  // Возвращает полный AxiosResponse (включая status) — нужно когда вызывающий код
  // должен различать коды (например 404 vs 500) без исключения при 4xx.
  public async getResponse<T = unknown>(
    url: string,
    queryParams?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.get<T>(url, { ...config, params: queryParams })
    } catch (error) {
      console.error(`GET (raw) request to ${url} failed:`, error)
      throw error
    }
  }

  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.post<T>(url, data, config)
    } catch (error) {
      console.error(`POST request to ${url} failed:`, error)
      throw error
    }
  }

  public async postForm<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.postForm<T>(url, data, config)
    } catch (error) {
      console.error(`POST form request to ${url} failed:`, error)
      throw error
    }
  }

  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.put<T>(url, data, config)
    } catch (error) {
      console.error(`PUT request to ${url} failed:`, error)
      throw error
    }
  }

  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.patch<T>(url, data, config)
    } catch (error) {
      console.error(`PATCH request to ${url} failed:`, error)
      throw error
    }
  }

  public async putForm<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.putForm<T>(url, data, config)
    } catch (error) {
      console.error(`PUT form request to ${url} failed:`, error)
      throw error
    }
  }

  public async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      return await this.instance.delete<T>(url, config)
    } catch (error) {
      console.error(`DELETE request to ${url} failed:`, error)
      throw error
    }
  }
}

const httpClientInstance = new HttpClient()
export default httpClientInstance
